import { Component, OnInit, ViewChild } from '@angular/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { TranslateService } from '@ngx-translate/core';
import { ModalComponent } from '../../../core/components/modal/modal.component';
import { ToastService } from 'core/services/toast.service';
import { ClientsActivationSettings } from './clients-activation-settings';
import { ClientsActivationWidgetService } from './clients-activation-widget.service';

@Component({
  selector: 'lms-clients-activation-widget',
  templateUrl: './clients-activation-widget.component.html',
  styleUrls: ['./clients-activation-widget.component.scss'],
  providers: [ClientsActivationWidgetService]
})
export class ClientsActivationWidgetComponent implements OnInit {
  @ViewChild('activationTableModal', { static: false }) public activationTableModal: ModalComponent;
  clientsActivationSettings: ClientsActivationSettings;
  dataSource: TableDatasource;
  validators: any[] = [];
  touched: boolean = false;

  constructor(
    private clientsActivationService: ClientsActivationWidgetService,
    private translate: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.clientsActivationService.getClientsActivationSettings().then((settings) => {
      this.clientsActivationSettings = settings;
    });
  }

  open() {
    this.createDatasource();
    this.activationTableModal.open();
  }

  close() {
    this.activationTableModal.close();
    this.dataSource = null;
  }

  createDatasource() {
    this.touched = false;
    this.validators = [];
    this.dataSource = new TableDatasource({
      sort: 'name',
      aggregates: ['clients'],
      populate: 'partner',
      columns: this.getColumns(),
      docs: []
    });
    if (this.clientsActivationSettings?.activationRanges?.length) {
      this.clientsActivationSettings.activationRanges.forEach((range) => this.addRow(range));
      this.dataSource.refresh();
    }
  }

  getColumns() {
    return [
      {
        source: 'min',
        dataType: DataTypes.number,
        label: this.translate.instant('settings.clients.activationWidget.min'),
        cellRenderer: this.getCellRenderer('min')
      },
      {
        source: 'max',
        dataType: DataTypes.number,
        label: this.translate.instant('settings.clients.activationWidget.max'),
        cellRenderer: this.getCellRenderer('max')
      },
      {
        source: 'activationGoal',
        dataType: DataTypes.number,
        label: this.translate.instant('settings.clients.activationWidget.activationGoal'),
        cellRenderer: this.getCellRenderer('activationGoal')
      },
      {
        alignment: 'right',
        style: { width: '75px' },
        cellRenderer: (renderer, value, data, row) => {
          return renderer`<gl-button slot="target" icon icon-center="glicon-trash" @click="${() =>
            this.deleteRow(data, row)}"></gl-button>`;
        }
      }
    ];
  }

  getCellRenderer(field) {
    return (renderer, value, data, row) => {
      return renderer`
        <gl-input inline 
          type="number" 
          __small
          min="0" 
          max="${field === 'activationGoal' ? 100 : null}"
          .value="${value}"
          .errorMessage="${this.validate(field, value, row)}"
          @onChange="${(ev) => this.onUpdateRow(ev, field, row)}"
        >
        </gl-input>`;
    };
  }

  addRow(data: any, dirty?: boolean) {
    this.validators.push({ valid: false, touched: new Set(dirty ? Object.keys(data) : null), errors: {} });
    this.dataSource.docs.push(data);
  }

  createEmptyRow() {
    this.touched = true;
    this.dataSource.refresh();
    this.addRow({});
  }

  deleteRow(data, row) {
    this.touched = true;
    this.validators.splice(row, 1);
    this.dataSource.docs.splice(row, 1);
    this.dataSource.refresh();
  }

  onUpdateRow(event, field, row) {
    this.touched = true;
    this.validators[row].touched.add(field);
    // important to create new object to re-evaluate complete row
    const newData = { ...this.dataSource.docs[row] };
    newData[field] = event.detail.value;
    this.dataSource.docs[row] = newData;
    this.dataSource.refresh();
  }

  onPaste(event) {
    const clipText = event.clipboardData.getData('Text');
    const data = clipText.split('\n').map((line) => line.split('\t'));
    if (data.length > 1 && data[0].length > 1) {
      event.preventDefault();
      data.forEach((row) => {
        const range = {};
        this.dataSource.columns
          .filter((col) => !!col.source)
          .forEach((col, index) => (range[col.source] = parseInt(row[index])));
        this.addRow(range, true);
      });
      this.dataSource.refresh();
    }
  }

  validate(field, value, row) {
    const data = this.dataSource.docs[row];
    const validator = this.validators[row];

    let errorMessage;
    if (value == null || !`${value}`.trim().length) {
      errorMessage = this.translate.instant('error.required');
    } else if (value < 0) {
      errorMessage = this.translate.instant('error.minValue', { limit: 0 });
    } else if (field === 'activationGoal' && value > 100) {
      errorMessage = this.translate.instant('error.maxValue', { limit: 100 });
    } else if (field === 'min' && data.max != null && value > data.max) {
      errorMessage = this.translate.instant('error.maxValue', { limit: data.max });
    } else if (field === 'max' && data.min != null && value < data.min) {
      errorMessage = this.translate.instant('error.minValue', { limit: data.min });
    } else if (['min', 'max'].includes(field)) {
      const ranges = this.dataSource.docs.filter(
        (range, index) =>
          index !== row && range.min != null && range.min <= value && range.max != null && range.max >= value
      );
      if (ranges.length > 0) {
        errorMessage = this.translate.instant('error.rangesOverlap');
      }
    }

    if (errorMessage) {
      validator.errors[field] = errorMessage;
    } else {
      delete validator.errors[field];
    }
    validator.valid = Object.values(validator.errors).length === 0;

    // only return error when field has been touched
    if (validator.touched.has(field)) {
      return errorMessage;
    }
  }

  get saveDisabled() {
    if (this.touched && this.validators?.length) {
      return this.validators.some((validator) => !validator.valid);
    }
    return true;
  }

  save() {
    this.clientsActivationService
      .updateClientsActivationSettings(this.dataSource.docs)
      .then((clientsActivationSettings) => {
        this.clientsActivationSettings = clientsActivationSettings;
        this.toastService.showOk('', 3000, false, null, this.toastService.MODAL_CONTEXT);
        this.createDatasource();
      })
      .catch((error) => this.toastService.showError(error, '', true, null, this.toastService.MODAL_CONTEXT));
  }
}
